import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Server, 
  Shield, 
  Network, 
  Users, 
  CheckCircle, 
  ArrowRight,
  Monitor,
  Cloud,
  Lock,
  Settings,
  Database,
  Wifi
} from "lucide-react";
import { services } from "../mock/data";

const Services = () => {
  const serviceCategories = [
    {
      id: "infrastructure",
      name: "IT-Infrastruktur",
      icon: Server,
      description: "Moderne und skalierbare IT-Systeme",
      headerImage: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31",
      services: [
        {
          title: "Server & Storage",
          description: "Hochverfügbare Server-Lösungen und Storage-Systeme",
          features: ["Dell PowerEdge Server", "SAN/NAS Systeme", "RAID-Konfigurationen", "Disaster Recovery"],
          image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8"
        },
        {
          title: "Virtualisierung",
          description: "VMware und Hyper-V Virtualisierungslösungen",
          features: ["vSphere Cluster", "Virtual Desktop Infrastructure", "Container-Technologie", "Cloud Migration"],
          image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxJVCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzYwMDAyMDQ4fDA&ixlib=rb-4.1.0&q=85"
        },
        {
          title: "Backup & Recovery",
          description: "Zuverlässige Datensicherung und Wiederherstellung",
          features: ["Automatisierte Backups", "Cloud Backup", "Bare Metal Recovery", "Retention Policies"],
          image: "https://images.unsplash.com/photo-1551808525-51a94da548ce"
        }
      ]
    },
    {
      id: "security",
      name: "IT-Sicherheit",
      icon: Shield,
      description: "Umfassender Schutz vor Cyberbedrohungen",
      headerImage: "https://images.unsplash.com/photo-1563013544-824ae1b704d3",
      services: [
        {
          title: "Firewall Management",
          description: "Next-Generation Firewalls und Sicherheitsrichtlinien",
          features: ["SonicWall/Fortinet", "Application Control", "Intrusion Prevention", "VPN-Gateways"],
          image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b"
        },
        {
          title: "Endpoint Security",
          description: "Schutz aller Endgeräte im Unternehmen",
          features: ["Antivirus/Anti-Malware", "EDR-Lösungen", "Mobile Device Management", "Patch Management"],
          image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3"
        },
        {
          title: "Security Monitoring", 
          description: "Kontinuierliche Überwachung der IT-Sicherheit",
          features: ["SIEM-Systeme", "Log-Analyse", "Threat Detection", "Incident Response"],
          image: "https://images.unsplash.com/photo-1510915361894-db8b60106cb1"
        }
      ]
    },
    {
      id: "network",
      name: "Netzwerkmanagement",
      icon: Network,
      description: "Zuverlässige Netzwerkinfrastrukturen",
      headerImage: "https://images.unsplash.com/photo-1551808525-51a94da548ce",
      services: [
        {
          title: "LAN/WAN Design",
          description: "Professionelle Netzwerkplanung und -implementierung",
          features: ["Cisco/HP Switches", "VLAN-Segmentierung", "QoS-Implementierung", "Redundante Verbindungen"],
          image: "https://images.unsplash.com/photo-1551808525-51a94da548ce"
        },
        {
          title: "WLAN-Lösungen",
          description: "Moderne drahtlose Netzwerke mit WiFi 6",
          features: ["Enterprise Access Points", "Controller-basiert", "Guest Network", "Roaming-Optimierung"],
          image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8"
        },
        {
          title: "Network Monitoring",
          description: "Kontinuierliche Netzwerküberwachung",
          features: ["SNMP-Monitoring", "Bandwidth-Analyse", "Performance-Metriken", "Alerting-Systeme"],
          image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8"
        }
      ]
    },
    {
      id: "consulting",
      name: "IT-Beratung",
      icon: Users,
      description: "Strategische IT-Beratung und Consulting",
      headerImage: "https://images.unsplash.com/photo-1557804506-669a67965ba0",
      services: [
        {
          title: "IT-Assessment",
          description: "Analyse der aktuellen IT-Landschaft",
          features: ["Infrastructure Review", "Security Assessment", "Performance-Analyse", "Compliance Check"],
          image: "https://images.unsplash.com/photo-1573166364366-3f4f8b1857ea"
        },
        {
          title: "Digital Strategy",
          description: "Roadmap für die digitale Transformation",
          features: ["Technology Planning", "Cloud Strategy", "Migration Planning", "ROI-Optimierung"],
          image: "https://images.unsplash.com/photo-1557804506-669a67965ba0"
        },
        {
          title: "Change Management",
          description: "Begleitung bei IT-Veränderungsprozessen",
          features: ["User Training", "Process Design", "Communication", "Go-Live Support"],
          image: "https://images.unsplash.com/photo-1616587656977-ac36a5a430bc"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <Badge className="bg-blue-100 text-blue-800">IT-Dienstleistungen</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
            IT-Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            IT-Infrastruktur, Cybersicherheit und Netzwerkmanagement aus einer Hand.
          </p>
        </div>

        {/* Service Categories Tabs */}
        <Tabs defaultValue="infrastructure" className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-12">
            {serviceCategories.map((category) => (
              <TabsTrigger 
                key={category.id} 
                value={category.id}
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              >
                <category.icon className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">{category.name}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {serviceCategories.map((category) => (
            <TabsContent key={category.id} value={category.id} className="space-y-8">
              {/* Category Header */}
              <div className="text-center space-y-4 mb-12">
                <div className="relative rounded-2xl overflow-hidden mb-8">
                  <img 
                    src={category.headerImage} 
                    alt={category.name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute inset-0 bg-blue-900/60 flex items-center justify-center">
                    <div className="text-center text-white">
                      <category.icon className="h-16 w-16 mx-auto mb-4" />
                      <h2 className="text-3xl font-bold">{category.name}</h2>
                      <p className="text-lg">{category.description}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Services Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {category.services.map((service, index) => (
                  <Card key={index} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                      <CardDescription>{service.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Additional Services */}
        <section className="mt-20 p-8 bg-gray-50 rounded-2xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Weitere Spezialdienste</h2>
            <p className="text-lg text-gray-600">
              Ergänzende Services für spezielle Anforderungen
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <Monitor className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>Controlling & Monitoring</CardTitle>
                <CardDescription>
                  Kontinuierliche Überwachung Ihrer IT-Systeme
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  <li>• System-Monitoring</li>
                  <li>• Performance-Dashboards</li>
                  <li>• Automated Reporting</li>
                  <li>• Proactive Alerting</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Lock className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>Datenschutz & Compliance</CardTitle>
                <CardDescription>
                  DSGVO-konforme Lösungen und Compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  <li>• DSGVO-Compliance</li>
                  <li>• ISO 27001 Beratung</li>
                  <li>• Audit-Unterstützung</li>
                  <li>• Privacy by Design</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Cloud className="h-8 w-8 text-blue-600 mb-2" />
                <CardTitle>Cloud Services</CardTitle>
                <CardDescription>
                  Migration und Management von Cloud-Lösungen
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  <li>• Azure/AWS Migration</li>
                  <li>• Hybrid Cloud Setup</li>
                  <li>• Cloud Optimization</li>
                  <li>• Multi-Cloud Management</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-20 text-center space-y-8 bg-blue-600 p-12 rounded-2xl">
          <h2 className="text-3xl font-bold text-white">
            Benötigen Sie individuelle IT-Lösungen?
          </h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Lassen Sie uns gemeinsam die perfekte IT-Strategie für Ihr 
            Unternehmen entwickeln. Kontaktieren Sie uns für eine kostenlose Beratung.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/contact">
                Kostenlose Beratung anfragen
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-blue-600" 
              asChild
            >
              <Link to="/service-models">
                Service-Modelle ansehen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Services;